/**
 * 🌾 RoamingZonePage — V4 Dragonious Castle (multi-room)
 *
 * Screens (each its own walkable space, corridor = fade transition):
 *   • courtyard — hub. Fountain center. Doors around the perimeter.
 *   • library   — Grandma Cari (Pitbull) sits at the reading desk.
 *   • diner     — David's Diner. Walkable, no NPCs yet (owner: "Gym later").
 *
 * Owner rulings (Feb 2026, latest):
 *   • Tomb of Awakening has NO player-operated entrance. It is reached
 *     ONLY via the Chronophage's story reset — a Tomb-only rule, story-
 *     driven, not a mechanic. Do not build any manual entrance here.
 *   • Hub Fountain is a WATER SOURCE for fishing. Interacting requires a
 *     fishing pole in the bag; without one, show a "need a pole" prompt.
 *   • Fountain in the Tomb is one-way OUT — the exit, never an entrance.
 *   • Front Castle Gate leads outside → choice: Wilds of Aethelgard OR
 *     The Oasis (fishing, keeps its music).
 *   • Anthropomorphic characters: Cari = Pitbull grandma; Nathaniel = human
 *     honey-badger silhouette.
 *   • Build slowly — Library + Diner first. Gym / other rooms later.
 *
 * Do not extend beyond this spec without owner sign-off.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const W = 960;
const H = 640;
const SPEED = 120;
const HERO_W = 48;
const HERO_H = 64;
const HERO_R = 14;
const EXIT_BAND = 36;
const FADE_MS = 380;

const isTouchDevice = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(pointer: coarse)").matches || "ontouchstart" in window);

/* ── Courtyard doors ─────────────────────────────────────────────────── *
 * Two kinds: room-doors (navigate within roam zone via `roomTo`) or
 * page-doors (navigate to another route via `to`). visualOnly doors
 * are grayed placeholders. */
const COURTYARD_DOORS = [
  { id: "library",       label: "Cari's Athenaeum",        icon: "📚", x: 130, y: 90,  w: 90, h: 96, roomTo: "library",  hint: "Grandma Cari is inside." },
  { id: "town-hall",     label: "Town Hall",               icon: "🏛️", x: 340, y: 90,  w: 96, h: 96, to: "/town-hall",     hint: "Taylor-Nightingale & Chief Blanks." },
  { id: "battlegrounds", label: "Battleground",            icon: "⚔️", x: 540, y: 90,  w: 90, h: 96, to: "/battlegrounds", hint: "Warband skirmishes." },
  { id: "coliseum",      label: "The Coliseum",            icon: "🏟️", x: 740, y: 90,  w: 96, h: 96, to: "/coliseum",      hint: "4v4 PvP & Party Dungeon." },
  { id: "guild",         label: "Bird of Prey Guild Hall", icon: "🦅", x: 900, y: 260, w: 90, h: 96, to: "/guild",         hint: "Guild missions & skills." },
  { id: "diner",         label: "Phoenix Flame Diner",     icon: "🐦‍🔥", x: 900, y: 480, w: 90, h: 96, roomTo: "diner",      hint: "Chef David + Nathaniel." },
  { id: "gym",           label: "The Peacock Gym",         icon: "🦚", x: 340, y: 260, w: 96, h: 96, roomTo: "gym",        hint: "Iron and honest sweat." },
  { id: "marketplace",   label: "Marketplace",             icon: "🛒", x: 60,  y: 260, w: 90, h: 96, visualOnly: true,     hint: "Visual only — not connected yet." },
  { id: "auction-house", label: "Auction House",           icon: "🎪", x: 60,  y: 480, w: 90, h: 96, to: "/auction-house", hint: "Bid on equipment & rare drops." },
  // 🏰 Front Castle Gate — leads outside. Modal picker on interact.
  { id: "front-gate",    label: "Front Castle Gate",       icon: "🏰", x: 480, y: 592, w: 140, h: 72, isFrontGate: true,   hint: "Outside — Wilds or Oasis." },
];

const FOUNTAIN = { x: 480, y: 340, r: 92 };

// 🕒 Small helper for the hub peer nameplates. Returns "now", "2m ago",
// "1h ago" etc. Falls back to empty when the timestamp is missing.
function _formatLastSeen(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return "now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/* ── Collision map per screen ────────────────────────────────────────── */
const SCREENS = {
  courtyard: {
    title: "Castle Courtyard · The Fountain",
    spawn: { x: 480, y: 500 },
    exits: [],      // exits only via door interact
    collisions: [
      { type: "circle", x: FOUNTAIN.x, y: FOUNTAIN.y, r: FOUNTAIN.r },
      ...COURTYARD_DOORS.map((d) => ({ type: "rect", x: d.x - d.w / 2, y: d.y - d.h / 2, w: d.w, h: d.h })),
      { type: "rect", x: -60, y: -60, w: W + 120, h: 60 },
      { type: "rect", x: -60, y: H,   w: W + 120, h: 60 },
      { type: "rect", x: -60, y: -60, w: 60, h: H + 120 },
      { type: "rect", x: W,   y: -60, w: 60, h: H + 120 },
    ],
  },
  library: {
    title: "Cari's Athenaeum",
    spawn: { x: 480, y: 560 },
    exits: [
      { x: 460, y: H - EXIT_BAND, w: 40, h: EXIT_BAND + 20, back: "courtyard", spawn: { x: 480, y: 200 } },
    ],
    collisions: [
      // Reading desk (Cari sits behind it — north side of desk)
      { type: "rect", x: 380, y: 260, w: 200, h: 60 },
      // Long shelves along the top wall
      { type: "rect", x: 20,  y: 60, w: 920, h: 60 },
      // Side bookshelves
      { type: "rect", x: 20,  y: 140, w: 90, h: 320 },
      { type: "rect", x: 850, y: 140, w: 90, h: 320 },
      // Reading rug pillars (decorative)
      { type: "rect", x: 240, y: 460, w: 60, h: 50 },
      { type: "rect", x: 660, y: 460, w: 60, h: 50 },
      // World borders (leave gap at bottom for exit)
      { type: "rect", x: -60, y: -60, w: W + 120, h: 60 },
      { type: "rect", x: -60, y: H,   w: 460, h: 60 },
      { type: "rect", x: 500, y: H,   w: W + 120 - 500, h: 60 },
      { type: "rect", x: -60, y: -60, w: 60, h: H + 120 },
      { type: "rect", x: W,   y: -60, w: 60, h: H + 120 },
    ],
    npcs: [
      {
        id: "cari",
        kind: "pitbull-grandma",
        name: "Grandma Cari",
        x: 480, y: 250,   // behind desk
        // 🃏 Oct 2026 owner directive — flagged as a DFCL duelist in the
        // catalog so future gameplay (and the dossier) can find her. No
        // "challenge" button yet; dragonious_fighters gameplay comes later.
        dfcl_duelable: true,
        lines: [
          "Well, come in, come in — mind the stacks. My old ears twitch when the front gate creaks.",
          "You're the one who wandered up from the crypt. Sit. I've been keeping notes on the shadow.",
          "The Archive under this floor holds names the Circulation Desk forgets. Not tonight — but soon.",
          "For now: warm hands, warm tea, cold questions. Come find me here whenever the loop spins strange.",
        ],
      },
    ],
  },
  diner: {
    title: "Phoenix Flame Diner",
    spawn: { x: 480, y: 560 },
    exits: [
      { x: 460, y: H - EXIT_BAND, w: 40, h: EXIT_BAND + 20, back: "courtyard", spawn: { x: 880, y: 460 } },
    ],
    collisions: [
      // Grill / back wall
      { type: "rect", x: 20, y: 80, w: 920, h: 100 },
      // Chrome counter
      { type: "rect", x: 100, y: 260, w: 760, h: 40 },
      // Bar stools row (as separate circles so hero can weave)
      { type: "circle", x: 200, y: 340, r: 18 },
      { type: "circle", x: 320, y: 340, r: 18 },
      { type: "circle", x: 440, y: 340, r: 18 },
      { type: "circle", x: 560, y: 340, r: 18 },
      { type: "circle", x: 680, y: 340, r: 18 },
      { type: "circle", x: 800, y: 340, r: 18 },
      // Booths back-right
      { type: "rect", x: 620, y: 440, w: 220, h: 80 },
      // World borders (leave gap at bottom for exit)
      { type: "rect", x: -60, y: -60, w: W + 120, h: 60 },
      { type: "rect", x: -60, y: H,   w: 460, h: 60 },
      { type: "rect", x: 500, y: H,   w: W + 120 - 500, h: 60 },
      { type: "rect", x: -60, y: -60, w: 60, h: H + 120 },
      { type: "rect", x: W,   y: -60, w: 60, h: H + 120 },
    ],
    // 🍳 David & Nathaniel — both live at the Diner. David works the grill;
    // Nathaniel (honey-badger) is on the beat with him. Real Nathaniel is
    // here — only his SHADOW appears on the Tomb page.
    npcs: [
      {
        id: "david",
        kind: "chef-david",
        name: "Chef David Lauer",
        x: 300, y: 220,   // behind the grill line
        // 🃏 Oct 2026 — DFCL duelist flag (see Cari note above).
        dfcl_duelable: true,
        lines: [
          "Kitchen's open — order up when you're hungry. Corn salad's on the specials board.",
          "Nathaniel's been holed up here since the loop got sticky. He'll pretend he isn't, but he is.",
          "Whatever you do, don't run when you see the shadow at the fountain. It's already made its pass for the night.",
        ],
      },
      {
        id: "nathaniel",
        kind: "honey-badger",
        name: "Nathaniel Bucket",
        x: 720, y: 480,   // seated in the booth, back-right
        // 🃏 Oct 2026 — DFCL duelist flag (see Cari note above).
        dfcl_duelable: true,
        lines: [
          "You saw him at the fountain, huh. Yeah. The one who drops the pouch every loop.",
          "That's not me. That's a version of me the loop keeps sending to fail. I'm on the beat now — with David.",
          "Word from the honey-badger, kid: keep the 500 Chits. It's the only thing the shadow gets right.",
        ],
      },
    ],
  },
  gym: {
    title: "The Peacock Gym",
    spawn: { x: 480, y: 560 },
    exits: [
      { x: 460, y: H - EXIT_BAND, w: 40, h: EXIT_BAND + 20, back: "courtyard", spawn: { x: 340, y: 340 } },
    ],
    collisions: [
      // Weight rack along top wall
      { type: "rect", x: 40,  y: 80, w: 300, h: 60 },
      // Squat rack
      { type: "rect", x: 380, y: 80, w: 140, h: 100 },
      // Cardio row (treadmills as rects)
      { type: "rect", x: 560, y: 90, w: 100, h: 80 },
      { type: "rect", x: 690, y: 90, w: 100, h: 80 },
      { type: "rect", x: 820, y: 90, w: 100, h: 80 },
      // Punching bag (circle) center-left
      { type: "circle", x: 220, y: 300, r: 22 },
      // Floor mat pillars (bench + dumbbell rack)
      { type: "rect", x: 420, y: 320, w: 160, h: 40 },
      { type: "rect", x: 660, y: 320, w: 200, h: 40 },
      // World borders (leave gap at bottom for exit)
      { type: "rect", x: -60, y: -60, w: W + 120, h: 60 },
      { type: "rect", x: -60, y: H,   w: 460, h: 60 },
      { type: "rect", x: 500, y: H,   w: W + 120 - 500, h: 60 },
      { type: "rect", x: -60, y: -60, w: 60, h: H + 120 },
      { type: "rect", x: W,   y: -60, w: 60, h: H + 120 },
    ],
    npcs: [],   // No NPC assigned yet — owner will drop one when ready
  },
};

const collides = (px, py, screen) => {
  for (const s of SCREENS[screen].collisions) {
    if (s.type === "rect") {
      const nx = Math.max(s.x, Math.min(px, s.x + s.w));
      const ny = Math.max(s.y, Math.min(py, s.y + s.h));
      const dx = px - nx, dy = py - ny;
      if (dx * dx + dy * dy < HERO_R * HERO_R) return true;
    } else {
      const dx = px - s.x, dy = py - s.y;
      const r = s.r + HERO_R;
      if (dx * dx + dy * dy < r * r) return true;
    }
  }
  return false;
};

/* ── Chibi hero sprite ───────────────────────────────────────────────── */
const HeroSprite = ({ facing, moving, frame, bob }) => {
  const stepUp = moving ? (frame === 1 ? -3 : 0) : 0;
  const stepDn = moving ? (frame === 1 ? 0 : frame === 2 ? -3 : 0) : 0;
  const bobY = !moving ? bob : 0;
  const showFace = facing !== "up";
  return (
    <svg width={HERO_W} height={HERO_H} viewBox="0 0 48 64" style={{ transform: `translateY(${bobY}px)` }}>
      <ellipse cx="24" cy="60" rx="12" ry="3" fill="rgba(0,0,0,0.35)" />
      <rect x="16" y={44 + stepUp} width="6" height="12" rx="2" fill="#2c3e50" />
      <rect x="26" y={44 + stepDn} width="6" height="12" rx="2" fill="#2c3e50" />
      <rect x="12" y="28" width="24" height="20" rx="6" fill="#3b82f6" stroke="#1e3a8a" strokeWidth="1.2" />
      <rect x="12" y="42" width="24" height="3" fill="#78350f" />
      {(facing === "up" || facing === "down") && (
        <>
          <rect x="8"  y={30 + (moving ? (frame === 1 ? -1 : 1) : 0)} width="5" height="12" rx="2" fill="#3b82f6" />
          <rect x="35" y={30 + (moving ? (frame === 2 ? -1 : 1) : 0)} width="5" height="12" rx="2" fill="#3b82f6" />
        </>
      )}
      {facing === "left"  && <rect x="8"  y="30" width="5" height="12" rx="2" fill="#3b82f6" />}
      {facing === "right" && <rect x="35" y="30" width="5" height="12" rx="2" fill="#3b82f6" />}
      <circle cx="24" cy="18" r="12" fill="#fcd7b6" stroke="#4a2a12" strokeWidth="1.2" />
      {facing === "up"
        ? <path d="M 12 18 Q 24 4 36 18 L 36 12 Q 24 2 12 12 Z" fill="#5a2a12" />
        : <path d="M 12 16 Q 24 2 36 16 L 36 12 Q 24 4 12 12 Z" fill="#5a2a12" />}
      {showFace && (
        <>
          <circle cx="20" cy="20" r="1.4" fill="#1f2937" />
          <circle cx="28" cy="20" r="1.4" fill="#1f2937" />
          <path d="M 22 24 Q 24 26 26 24" stroke="#1f2937" strokeWidth="1" fill="none" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
};

/* ── Anthropomorphic Cari — Pitbull Grandma ──────────────────────────── */
const PitbullCariSprite = () => (
  <svg width={HERO_W} height={HERO_H + 8} viewBox="0 0 48 72">
    <ellipse cx="24" cy="68" rx="14" ry="3.5" fill="rgba(0,0,0,0.35)" />
    {/* Long robe */}
    <path d="M 8 52 Q 24 46 40 52 L 42 66 Q 24 68 6 66 Z" fill="#4d7c0f" stroke="#1a2e05" strokeWidth="1.2" />
    <rect x="10" y="32" width="28" height="22" rx="6" fill="#65a30d" stroke="#1a2e05" strokeWidth="1.2" />
    {/* Shawl */}
    <path d="M 8 34 Q 24 30 40 34 L 38 44 Q 24 40 10 44 Z" fill="#a3e635" opacity="0.9" />
    {/* Paw-hands (dog paws holding a book) */}
    <rect x="6"  y="36" width="6" height="14" rx="3" fill="#e8c99b" stroke="#4a2a12" strokeWidth="1" />
    <rect x="36" y="36" width="6" height="14" rx="3" fill="#e8c99b" stroke="#4a2a12" strokeWidth="1" />
    {/* Small book */}
    <rect x="16" y="42" width="16" height="10" rx="1" fill="#7f1d1d" stroke="#1a0a0a" strokeWidth="0.8" />
    <rect x="16" y="46" width="16" height="1" fill="#fef3c7" />
    {/* Pitbull head — squarish snout, folded ears */}
    <ellipse cx="24" cy="20" rx="14" ry="13" fill="#e8c99b" stroke="#4a2a12" strokeWidth="1.2" />
    {/* Snout */}
    <ellipse cx="24" cy="26" rx="8" ry="6" fill="#f2dbaf" stroke="#4a2a12" strokeWidth="1" />
    {/* Nose */}
    <ellipse cx="24" cy="24" rx="2.5" ry="1.8" fill="#1f2937" />
    {/* Mouth */}
    <path d="M 22 28 Q 24 30 26 28" stroke="#1f2937" strokeWidth="1" fill="none" strokeLinecap="round" />
    {/* White muzzle patch */}
    <ellipse cx="24" cy="28" rx="4" ry="1.6" fill="#faf3e0" opacity="0.7" />
    {/* Folded ears */}
    <path d="M 10 10 Q 8 4 15 6 L 14 14 Q 12 16 10 10 Z" fill="#c9a875" stroke="#4a2a12" strokeWidth="1" />
    <path d="M 38 10 Q 40 4 33 6 L 34 14 Q 36 16 38 10 Z" fill="#c9a875" stroke="#4a2a12" strokeWidth="1" />
    {/* Eye markings — brindle */}
    <path d="M 14 14 Q 16 12 20 14" stroke="#7c5a3a" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6" />
    <path d="M 28 14 Q 32 12 34 14" stroke="#7c5a3a" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.6" />
    {/* Eyes */}
    <circle cx="19" cy="18" r="1.8" fill="#1f2937" />
    <circle cx="29" cy="18" r="1.8" fill="#1f2937" />
    <circle cx="19.5" cy="17.5" r="0.6" fill="#faf3e0" />
    <circle cx="29.5" cy="17.5" r="0.6" fill="#faf3e0" />
    {/* Silver hair bun on top */}
    <circle cx="24" cy="6" r="6" fill="#f5f5f4" stroke="#4a2a12" strokeWidth="1" />
    <circle cx="24" cy="4" r="4" fill="#e7e5e4" />
  </svg>
);

/* ── Chef David Lauer — human chef (glasses, chef hat, apron) ────────── */
const ChefDavidSprite = () => (
  <svg width={HERO_W} height={HERO_H + 8} viewBox="0 0 48 72">
    <ellipse cx="24" cy="68" rx="14" ry="3.5" fill="rgba(0,0,0,0.35)" />
    {/* Chef pants */}
    <rect x="16" y="52" width="6" height="14" rx="2" fill="#525252" />
    <rect x="26" y="52" width="6" height="14" rx="2" fill="#525252" />
    {/* Apron / body */}
    <rect x="12" y="30" width="24" height="24" rx="4" fill="#f5f5f4" stroke="#292524" strokeWidth="1.2" />
    <rect x="12" y="30" width="24" height="3" fill="#292524" />
    {/* Apron string */}
    <rect x="12" y="42" width="24" height="2" fill="#a8a29e" />
    {/* Kitchen spatula in hand */}
    <rect x="6"  y="34" width="4" height="16" rx="1" fill="#fcd7b6" stroke="#4a2a12" strokeWidth="0.8" />
    <rect x="4"  y="30" width="8" height="4"  rx="1" fill="#a3a3a3" stroke="#404040" strokeWidth="0.8" />
    <rect x="38" y="34" width="4" height="16" rx="1" fill="#fcd7b6" stroke="#4a2a12" strokeWidth="0.8" />
    {/* Head */}
    <circle cx="24" cy="18" r="12" fill="#fcd7b6" stroke="#4a2a12" strokeWidth="1.2" />
    {/* Chef hat */}
    <ellipse cx="24" cy="6" rx="12" ry="4" fill="#f5f5f4" stroke="#292524" strokeWidth="1.2" />
    <path d="M 12 8 Q 12 -4 24 -2 Q 36 -4 36 8 Z" fill="#f5f5f4" stroke="#292524" strokeWidth="1.2" />
    <ellipse cx="24" cy="8" rx="12" ry="3" fill="#f5f5f4" stroke="#292524" strokeWidth="1.2" />
    {/* Glasses */}
    <circle cx="19" cy="18" r="3" stroke="#1f2937" strokeWidth="1" fill="none" />
    <circle cx="29" cy="18" r="3" stroke="#1f2937" strokeWidth="1" fill="none" />
    <line x1="22" y1="18" x2="26" y2="18" stroke="#1f2937" strokeWidth="1" />
    {/* Eyes behind glasses */}
    <circle cx="19" cy="18" r="1.2" fill="#1f2937" />
    <circle cx="29" cy="18" r="1.2" fill="#1f2937" />
    {/* Moustache */}
    <path d="M 18 24 Q 24 28 30 24" stroke="#3a1f0a" strokeWidth="2" fill="none" strokeLinecap="round" />
    {/* Smile */}
    <path d="M 21 27 Q 24 29 27 27" stroke="#1f2937" strokeWidth="0.8" fill="none" strokeLinecap="round" />
  </svg>
);

/* ── Honey-Badger Nathaniel — anthro, black+silver stripe, rusty bucket ── */
const HoneyBadgerNathanielSprite = () => (
  <svg width={HERO_W} height={HERO_H + 8} viewBox="0 0 48 72">
    <ellipse cx="24" cy="68" rx="14" ry="3.5" fill="rgba(0,0,0,0.35)" />
    {/* Legs */}
    <rect x="16" y="52" width="6" height="14" rx="2" fill="#1a1a1a" />
    <rect x="26" y="52" width="6" height="14" rx="2" fill="#1a1a1a" />
    {/* Body — black lower, silver stripe upper (canonical honey-badger) */}
    <rect x="12" y="32" width="24" height="22" rx="6" fill="#1a1a1a" stroke="#0a0a0a" strokeWidth="1.2" />
    <path d="M 12 32 Q 24 30 36 32 L 36 40 Q 24 38 12 40 Z" fill="#d6d3d1" />
    {/* Suspenders */}
    <rect x="15" y="32" width="2" height="22" fill="#78350f" />
    <rect x="31" y="32" width="2" height="22" fill="#78350f" />
    {/* Rusty bucket at hip (his namesake) */}
    <rect x="4"  y="42" width="8" height="10" rx="1" fill="#8b4513" stroke="#3a1f0a" strokeWidth="1" />
    <rect x="4"  y="42" width="8" height="2" fill="#5a3316" />
    <path d="M 5 42 Q 8 39 11 42" stroke="#3a1f0a" strokeWidth="1" fill="none" />
    {/* Arms */}
    <rect x="8"  y="34" width="5" height="12" rx="2" fill="#1a1a1a" />
    <rect x="35" y="34" width="5" height="12" rx="2" fill="#1a1a1a" />
    {/* Head — badger */}
    <ellipse cx="24" cy="20" rx="13" ry="12" fill="#1a1a1a" stroke="#0a0a0a" strokeWidth="1.2" />
    {/* Signature white stripe from forehead down muzzle */}
    <path d="M 24 8 L 20 30 L 28 30 Z" fill="#f5f5f4" />
    <path d="M 18 14 Q 24 12 30 14" stroke="#f5f5f4" strokeWidth="6" fill="none" strokeLinecap="round" />
    {/* Small round ears */}
    <circle cx="12" cy="12" r="3" fill="#1a1a1a" stroke="#0a0a0a" strokeWidth="1" />
    <circle cx="36" cy="12" r="3" fill="#1a1a1a" stroke="#0a0a0a" strokeWidth="1" />
    <circle cx="12" cy="12" r="1.4" fill="#7c2d12" />
    <circle cx="36" cy="12" r="1.4" fill="#7c2d12" />
    {/* Snout */}
    <ellipse cx="24" cy="26" rx="4" ry="3" fill="#1a1a1a" />
    <ellipse cx="24" cy="25" rx="2" ry="1.4" fill="#0a0a0a" />
    {/* Eyes — sharp, alert */}
    <circle cx="19" cy="20" r="1.6" fill="#fbbf24" />
    <circle cx="29" cy="20" r="1.6" fill="#fbbf24" />
    <circle cx="19" cy="20" r="0.7" fill="#0a0a0a" />
    <circle cx="29" cy="20" r="0.7" fill="#0a0a0a" />
    {/* Newsboy cap on top */}
    <path d="M 12 8 Q 24 0 36 8 L 38 12 L 10 12 Z" fill="#374151" stroke="#0a0a0a" strokeWidth="1" />
    <rect x="10" y="11" width="28" height="3" rx="1" fill="#1f2937" />
  </svg>
);

/* ── Door sprite (unchanged) ─────────────────────────────────────────── */
const DoorSprite = ({ door, near, visualOnly, isFrontGate }) => {
  if (isFrontGate) {
    return (
      <svg width={door.w} height={door.h} viewBox={`0 0 ${door.w} ${door.h}`}>
        {/* Wide arched gate */}
        <rect x="2" y="6" width={door.w - 4} height={door.h - 10} rx="24" fill="#3a2410" stroke="#1a0f04" strokeWidth="2" />
        <rect x="10" y="14" width={(door.w - 20) / 2 - 2} height={door.h - 26} rx="4"
              fill={near ? "#7c4a1f" : "#5c3618"} stroke={near ? "#fde047" : "#1a0f04"} strokeWidth={near ? 3 : 2}
              style={{ filter: near ? "drop-shadow(0 0 6px rgba(253,224,71,0.6))" : "none" }} />
        <rect x={door.w / 2 + 2} y="14" width={(door.w - 20) / 2 - 2} height={door.h - 26} rx="4"
              fill={near ? "#7c4a1f" : "#5c3618"} stroke={near ? "#fde047" : "#1a0f04"} strokeWidth={near ? 3 : 2}
              style={{ filter: near ? "drop-shadow(0 0 6px rgba(253,224,71,0.6))" : "none" }} />
        <text x={door.w / 2} y={door.h - 6} textAnchor="middle" fontSize="22">{door.icon}</text>
      </svg>
    );
  }
  return (
    <svg width={door.w} height={door.h} viewBox={`0 0 ${door.w} ${door.h}`}>
      <rect x="2" y="6" width={door.w - 4} height={door.h - 10} rx="10" fill="#3a2410" stroke="#1a0f04" strokeWidth="2" />
      <rect x="10" y="16" width={door.w - 20} height={door.h - 30} rx="6"
            fill={visualOnly ? "#4a3728" : "#7c4a1f"}
            stroke={near ? "#fde047" : "#1a0f04"} strokeWidth={near ? 3 : 2}
            style={{ filter: near ? "drop-shadow(0 0 6px rgba(253,224,71,0.6))" : "none" }} />
      <circle cx={door.w - 18} cy={door.h / 2} r="2.5" fill="#fcd34d" />
      <line x1="14" y1="24" x2={door.w - 14} y2="24" stroke="#5a3316" strokeWidth="1" opacity="0.6" />
      <text x={door.w / 2} y={door.h - 2} textAnchor="middle" fontSize="18">{door.icon}</text>
    </svg>
  );
};

/* ── Backgrounds ────────────────────────────────────────────────────── */
const CobbleBase = ({ children }) => (
  <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
    <defs>
      <radialGradient id="c-stone" cx="50%" cy="50%" r="80%">
        <stop offset="0%" stopColor="#8b8680" />
        <stop offset="60%" stopColor="#5c574f" />
        <stop offset="100%" stopColor="#3a352d" />
      </radialGradient>
    </defs>
    <rect width={W} height={H} fill="url(#c-stone)" />
    {Array.from({ length: 12 }).map((_, r) => (
      Array.from({ length: 18 }).map((_, c) => (
        <rect key={`${r}-${c}`} x={c * 56 + (r % 2 ? 28 : 0)} y={r * 56} width="52" height="52"
              fill="none" stroke="rgba(0,0,0,0.18)" strokeWidth="1" />
      ))
    ))}
    {children}
  </svg>
);

const CourtyardBackground = () => (
  <CobbleBase>
    <defs>
      <linearGradient id="c-water" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#93c5fd" />
        <stop offset="100%" stopColor="#1e40af" />
      </linearGradient>
    </defs>
    <circle cx={W / 2} cy={H / 2 + 20} r="200" fill="#a8968a" opacity="0.35" />
    <circle cx={W / 2} cy={H / 2 + 20} r="150" fill="#c7b8ac" opacity="0.35" />
    <path d="M 0 60 L 60 60 L 60 30 L 100 30 L 100 60 L 160 60 L 160 10 L 220 10 L 220 60 L 320 60 L 320 30 L 380 30 L 380 60 L 500 60 L 500 30 L 560 30 L 560 60 L 700 60 L 700 20 L 760 20 L 760 60 L 960 60 L 960 0 L 0 0 Z"
          fill="#2a2620" opacity="0.85" />
    {/* Stone Fountain */}
    <ellipse cx={FOUNTAIN.x} cy={FOUNTAIN.y} rx="110" ry="70" fill="#6b6259" stroke="#2a2620" strokeWidth="4" />
    <ellipse cx={FOUNTAIN.x} cy={FOUNTAIN.y} rx="94" ry="56" fill="url(#c-water)" stroke="#1e3a5f" strokeWidth="2" />
    <ellipse cx={FOUNTAIN.x} cy={FOUNTAIN.y} rx="34" ry="18" fill="#4a4238" stroke="#2a2620" strokeWidth="2" />
    <rect x={FOUNTAIN.x - 8} y={FOUNTAIN.y - 50} width="16" height="52" fill="#6b6259" stroke="#2a2620" strokeWidth="2" />
    <ellipse cx={FOUNTAIN.x} cy={FOUNTAIN.y - 50} rx="14" ry="6" fill="#8b8680" stroke="#2a2620" strokeWidth="2" />
    <path d={`M ${FOUNTAIN.x} ${FOUNTAIN.y - 52} Q ${FOUNTAIN.x - 4} ${FOUNTAIN.y - 80} ${FOUNTAIN.x} ${FOUNTAIN.y - 92} Q ${FOUNTAIN.x + 4} ${FOUNTAIN.y - 80} ${FOUNTAIN.x} ${FOUNTAIN.y - 52}`} fill="#dbeafe" opacity="0.85" />
    <ellipse cx={FOUNTAIN.x - 20} cy={FOUNTAIN.y - 20} rx="24" ry="8" fill="#ffffff" opacity="0.35" />
  </CobbleBase>
);

const LibraryBackground = () => (
  <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
    <defs>
      <linearGradient id="lib-floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5a3316" />
        <stop offset="100%" stopColor="#3a1f0a" />
      </linearGradient>
    </defs>
    {/* Wood floor */}
    <rect width={W} height={H} fill="url(#lib-floor)" />
    {Array.from({ length: 20 }).map((_, i) => (
      <line key={i} x1="0" y1={i * 32 + 8} x2={W} y2={i * 32 + 8} stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
    ))}
    {/* Red rug */}
    <ellipse cx={W / 2} cy={H / 2 + 30} rx="360" ry="140" fill="#7f1d1d" opacity="0.75" />
    <ellipse cx={W / 2} cy={H / 2 + 30} rx="330" ry="120" fill="none" stroke="#fbbf24" strokeWidth="4" opacity="0.65" />
    {/* Top shelf wall (colored spine bricks) */}
    <rect x="20" y="20" width="920" height="100" fill="#3a1f0a" stroke="#1a0a04" strokeWidth="3" />
    {Array.from({ length: 40 }).map((_, i) => (
      <rect key={i} x={26 + i * 23} y="28" width="20" height="64"
            fill={["#7f1d1d","#78350f","#365314","#1e3a8a","#7c2d12","#3b0764","#4c1d95","#831843"][i % 8]}
            stroke="#1a0a04" strokeWidth="0.5" />
    ))}
    {/* Side shelves */}
    <rect x="20" y="140" width="90" height="320" fill="#3a1f0a" stroke="#1a0a04" strokeWidth="3" />
    <rect x="850" y="140" width="90" height="320" fill="#3a1f0a" stroke="#1a0a04" strokeWidth="3" />
    {Array.from({ length: 6 }).map((_, i) => (
      <g key={i}>
        {Array.from({ length: 4 }).map((_, j) => (
          <rect key={j} x={26 + j * 22} y={148 + i * 52} width="18" height="48"
                fill={["#7f1d1d","#365314","#1e3a8a","#3b0764"][j % 4]} />
        ))}
        {Array.from({ length: 4 }).map((_, j) => (
          <rect key={`r${j}`} x={856 + j * 22} y={148 + i * 52} width="18" height="48"
                fill={["#7c2d12","#78350f","#831843","#4c1d95"][j % 4]} />
        ))}
      </g>
    ))}
    {/* Reading desk */}
    <rect x="380" y="260" width="200" height="60" fill="#8b4513" stroke="#3a1f0a" strokeWidth="3" />
    <rect x="380" y="260" width="200" height="8" fill="#a0522d" />
    <rect x="410" y="290" width="140" height="20" fill="#deb887" opacity="0.5" />
    {/* Candle on desk */}
    <rect x="560" y="248" width="4" height="14" fill="#fef3c7" />
    <path d="M 562 244 Q 558 240 562 234 Q 566 240 562 244 Z" fill="#f59e0b" />
    {/* Rug pillars (decorative candelabra) */}
    <rect x="240" y="460" width="60" height="50" fill="#4a2a12" stroke="#1a0f04" strokeWidth="2" />
    <path d="M 270 456 Q 265 450 270 442 Q 275 450 270 456" fill="#f59e0b" />
    <rect x="660" y="460" width="60" height="50" fill="#4a2a12" stroke="#1a0f04" strokeWidth="2" />
    <path d="M 690 456 Q 685 450 690 442 Q 695 450 690 456" fill="#f59e0b" />
    {/* Exit hint (bottom center gap = doorway to courtyard) */}
    <rect x="440" y={H - EXIT_BAND} width="80" height={EXIT_BAND} fill="#fde68a" opacity="0.22" />
    <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="12" fill="#fff8dc" fontFamily="ui-sans-serif" opacity="0.75">↓ Back to Courtyard</text>
  </svg>
);

const DinerBackground = () => (
  <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
    {/* Checker floor */}
    <rect width={W} height={H} fill="#1a1a1a" />
    {Array.from({ length: 20 }).map((_, r) => (
      Array.from({ length: 30 }).map((_, c) => (
        <rect key={`${r}-${c}`} x={c * 32} y={r * 32 + 200} width="32" height="32"
              fill={(r + c) % 2 === 0 ? "#f5f5f4" : "#1a1a1a"} />
      ))
    ))}
    {/* Back wall */}
    <rect x="0" y="0" width={W} height="200" fill="#7f1d1d" />
    {/* Grill / hood */}
    <rect x="20" y="80" width="920" height="100" fill="#2a2a2a" stroke="#0a0a0a" strokeWidth="3" />
    <rect x="20" y="80" width="920" height="18" fill="#4a4a4a" />
    {/* Neon DINER sign */}
    <text x={W / 2} y="60" textAnchor="middle" fontSize="42" fontWeight="900"
          fill="#f59e0b" style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 12px #ef4444" }}>
      DAVID'S DINER
    </text>
    {/* Chrome counter */}
    <rect x="100" y="260" width="760" height="40" fill="#d4d4d4" stroke="#525252" strokeWidth="2" />
    <rect x="100" y="256" width="760" height="6" fill="#f5f5f4" />
    {/* Bar stools */}
    {[200, 320, 440, 560, 680, 800].map((cx, i) => (
      <g key={i}>
        <rect x={cx - 3} y="340" width="6" height="20" fill="#525252" />
        <circle cx={cx} cy="340" r="16" fill="#7f1d1d" stroke="#450a0a" strokeWidth="2" />
        <circle cx={cx} cy="340" r="10" fill="#991b1b" />
      </g>
    ))}
    {/* Booth back-right */}
    <rect x="620" y="440" width="220" height="80" fill="#7f1d1d" stroke="#450a0a" strokeWidth="3" />
    <rect x="640" y="460" width="180" height="40" fill="#a8a29e" />
    {/* Exit hint */}
    <rect x="440" y={H - EXIT_BAND} width="80" height={EXIT_BAND} fill="#fde68a" opacity="0.22" />
    <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="12" fill="#fff8dc" fontFamily="ui-sans-serif" opacity="0.75">↓ Back to Courtyard</text>
  </svg>
);

/* ── Gym Background — iron rack, punching bag, squat rack, treadmills ── */
const GymBackground = () => (
  <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
    <defs>
      <linearGradient id="gym-floor" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#292524" />
        <stop offset="100%" stopColor="#111111" />
      </linearGradient>
    </defs>
    <rect width={W} height={H} fill="url(#gym-floor)" />
    {/* Rubber floor tiles */}
    {Array.from({ length: 20 }).map((_, r) => (
      Array.from({ length: 30 }).map((_, c) => (
        <rect key={`${r}-${c}`} x={c * 32} y={r * 32 + 200} width="32" height="32"
              fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      ))
    ))}
    {/* Weight rack (top-left) */}
    <rect x="40" y="80" width="300" height="60" fill="#404040" stroke="#0a0a0a" strokeWidth="3" />
    <rect x="40" y="80" width="300" height="6" fill="#525252" />
    {[70, 110, 150, 190, 230, 270, 310].map((cx, i) => (
      <g key={i}>
        <rect x={cx - 2} y="90" width="4" height="42" fill="#a3a3a3" />
        <circle cx={cx} cy="130" r="8" fill="#171717" stroke="#404040" strokeWidth="1" />
      </g>
    ))}
    {/* Squat rack */}
    <rect x="380" y="80" width="140" height="100" fill="#525252" stroke="#0a0a0a" strokeWidth="3" />
    <rect x="380" y="80" width="12" height="100" fill="#404040" />
    <rect x="508" y="80" width="12" height="100" fill="#404040" />
    <rect x="380" y="105" width="140" height="6" fill="#a3a3a3" />
    <rect x="380" y="140" width="140" height="6" fill="#a3a3a3" />
    {/* Barbell across squat rack */}
    <rect x="360" y="120" width="180" height="4" fill="#d4d4d4" />
    <rect x="360" y="115" width="8" height="16" fill="#171717" />
    <rect x="532" y="115" width="8" height="16" fill="#171717" />
    {/* Treadmills */}
    {[560, 690, 820].map((x, i) => (
      <g key={i}>
        <rect x={x} y="90" width="100" height="80" fill="#171717" stroke="#404040" strokeWidth="2" />
        <rect x={x + 10} y="130" width="80" height="30" fill="#0a0a0a" />
        <rect x={x + 20} y="98" width="60" height="26" fill="#1e3a8a" />
        <text x={x + 50} y="115" textAnchor="middle" fontSize="10" fill="#93c5fd" fontFamily="monospace">5.0 MPH</text>
      </g>
    ))}
    {/* Punching bag */}
    <line x1="220" y1="200" x2="220" y2="260" stroke="#a3a3a3" strokeWidth="2" />
    <ellipse cx="220" cy="300" rx="22" ry="34" fill="#7f1d1d" stroke="#450a0a" strokeWidth="3" />
    <rect x="210" y="270" width="20" height="6" fill="#a3a3a3" />
    {/* Weight bench */}
    <rect x="420" y="320" width="160" height="40" fill="#171717" stroke="#404040" strokeWidth="3" />
    <rect x="420" y="316" width="160" height="6" fill="#7f1d1d" />
    <rect x="440" y="360" width="6" height="30" fill="#404040" />
    <rect x="554" y="360" width="6" height="30" fill="#404040" />
    {/* Dumbbell rack */}
    <rect x="660" y="320" width="200" height="40" fill="#404040" stroke="#0a0a0a" strokeWidth="3" />
    {[680, 720, 760, 800, 840].map((cx, i) => (
      <g key={i}>
        <rect x={cx - 10} y="332" width="20" height="6" fill="#a3a3a3" />
        <circle cx={cx - 12} cy="335" r="6" fill="#171717" stroke="#404040" strokeWidth="1" />
        <circle cx={cx + 12} cy="335" r="6" fill="#171717" stroke="#404040" strokeWidth="1" />
      </g>
    ))}
    {/* Motivational poster (top-center) */}
    <rect x="380" y="20" width="200" height="40" fill="#0f172a" stroke="#fbbf24" strokeWidth="2" />
    <text x="480" y="46" textAnchor="middle" fontSize="14" fontWeight="900" fill="#fbbf24" style={{ fontFamily: "Anton, sans-serif" }}>THE PEACOCK GYM</text>
    {/* Exit hint */}
    <rect x="440" y={H - EXIT_BAND} width="80" height={EXIT_BAND} fill="#fde68a" opacity="0.22" />
    <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="12" fill="#fff8dc" fontFamily="ui-sans-serif" opacity="0.75">↓ Back to Courtyard</text>
  </svg>
);

/* ── Main page ───────────────────────────────────────────────────────── */
export default function RoamingZonePage() {
  const nav = useNavigate();
  const stageRef = useRef(null);
  const rafRef = useRef(0);

  const [screen, setScreen] = useState("courtyard");
  const [fading, setFading] = useState(false);
  const [frontGateOpen, setFrontGateOpen] = useState(false);

  const heroRef = useRef({ x: SCREENS.courtyard.spawn.x, y: SCREENS.courtyard.spawn.y });
  const [, setTick] = useState(0);
  const facingRef = useRef("up");
  const movingRef = useRef(false);
  const walkFrameRef = useRef(0);
  const walkTimerRef = useRef(0);
  const idleBobRef = useRef(0);

  const keys = useRef({});
  const joyVec = useRef({ x: 0, y: 0 });
  const targetRef = useRef(null);
  const rippleRef = useRef(null);

  const [nearDoor, setNearDoor] = useState(null);
  const [nearFountain, setNearFountain] = useState(false);
  const [nearNpc, setNearNpc] = useState(null);
  const [dialogue, setDialogue] = useState(null);
  const typewriterRef = useRef({ full: "", i: 0, last: 0 });

  const [isTouch] = useState(() => isTouchDevice());

  const [scale, setScale] = useState(1);
  // 👥 Feb 2026 owner spec (#4, #9) + Live-presence extension:
  //   Hub renders ALL online users with their LIVE roam position
  //   (screen + x/y + facing). Peers walk around, not statues.
  //   Each client broadcasts its own position every ~2s via a POST
  //   heartbeat, and polls the peer list every ~2s. Positions are
  //   smoothly interpolated between polls so motion looks fluid.
  const [peers, setPeers] = useState([]);
  // peer position render-state (user_id → {x, y, facing, tx, ty, tf, pet_icon, name, handle, social_handle, last_seen_at})
  // tx/ty/tf = latest *target* received from the server; x/y/facing = currently-rendered (interpolated) values.
  const peerStateRef = useRef(new Map());

  // 📡 Peer poll — every 2 s. Only pulls peers in the SAME room.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/roam/online`, {
          params: { screen },
        });
        if (cancelled) return;
        const list = Array.isArray(data?.peers) ? data.peers : [];
        setPeers(list);
        // Fold the fresh targets into the render state. New peers spawn
        // at their target so they don't shoot in from (0,0).
        const map = peerStateRef.current;
        const seen = new Set();
        for (const p of list) {
          seen.add(p.user_id);
          const pos = p.roam_position || { x: 480, y: 500, facing: "down" };
          const prev = map.get(p.user_id);
          map.set(p.user_id, {
            name: p.name,
            handle: p.handle,
            social_handle: p.social_handle,
            pet_icon: p.pet_icon,
            last_seen_at: p.last_seen_at,
            // target
            tx: pos.x, ty: pos.y, tf: pos.facing,
            // current (keep prior position on update; spawn at target on first sight)
            x: prev ? prev.x : pos.x,
            y: prev ? prev.y : pos.y,
            facing: prev ? prev.facing : pos.facing,
          });
        }
        // Prune peers no longer in-room/online.
        for (const uid of Array.from(map.keys())) {
          if (!seen.has(uid)) map.delete(uid);
        }
      } catch { /* silent — user may be signed out */ }
    };
    load();
    const t = setInterval(load, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [screen]);

  // 📡 Broadcast our own position every 2s via POST heartbeat.
  // Only sends when the component is mounted and keeps the latest
  // live screen/x/y/facing. Fire-and-forget — ignore errors.
  useEffect(() => {
    let cancelled = false;
    const beat = async () => {
      try {
        await axios.post(`${API}/roam/heartbeat`, {
          screen,
          x: Math.round(heroRef.current.x),
          y: Math.round(heroRef.current.y),
          facing: facingRef.current,
        });
      } catch { /* silent */ }
    };
    beat();
    const t = setInterval(() => { if (!cancelled) beat(); }, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [screen]);
  useEffect(() => {
    const onResize = () => {
      const el = stageRef.current;
      if (!el) return;
      const availW = el.clientWidth;
      const availH = Math.min(window.innerHeight - 24, availW * (H / W));
      const s = Math.min(availW / W, availH / H, 1);
      setScale(s);
    };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ── Dialogue helpers ─── */
  const openDialogue = (npc) => {
    setDialogue({ npc, lineIdx: 0, typed: "" });
    typewriterRef.current = { full: npc.lines[0], i: 0, last: performance.now() };
  };
  const advanceDialogue = () => {
    if (!dialogue) return;
    const cur = typewriterRef.current;
    if (cur.i < cur.full.length) {
      typewriterRef.current = { ...cur, i: cur.full.length };
      setDialogue((d) => ({ ...d, typed: cur.full }));
      return;
    }
    const nextIdx = dialogue.lineIdx + 1;
    if (nextIdx < dialogue.npc.lines.length) {
      const line = dialogue.npc.lines[nextIdx];
      typewriterRef.current = { full: line, i: 0, last: performance.now() };
      setDialogue({ npc: dialogue.npc, lineIdx: nextIdx, typed: "" });
    } else {
      setDialogue(null);
    }
  };

  /* ── Screen transition ─── */
  const transitionTo = useCallback((toScreen, spawn) => {
    if (fading) return;
    setFading(true);
    setTimeout(() => {
      heroRef.current = { ...spawn };
      setScreen(toScreen);
      targetRef.current = null;
      setTimeout(() => setFading(false), FADE_MS / 2);
    }, FADE_MS / 2);
  }, [fading]);

  /* ── Door / interaction dispatcher ─── */
  const enterDoor = useCallback((door) => {
    if (!door) return;
    if (door.visualOnly) { toast(`🔒 ${door.label} — ${door.hint}`, { duration: 2200 }); return; }
    if (door.isFrontGate) { setFrontGateOpen(true); return; }
    if (door.roomTo) {
      const target = SCREENS[door.roomTo];
      transitionTo(door.roomTo, target.spawn);
      return;
    }
    if (door.to) nav(door.to);
  }, [nav, transitionTo]);

  const tryInteract = useCallback(() => {
    if (dialogue) { advanceDialogue(); return; }
    if (screen === "courtyard" && nearFountain) {
      // 🏞️ Hub Fountain — a water source for fishing.
      // Feb 2026 owner canon: the Tomb of Awakening has NO player-operated
      // entrance. It is reached only via the Chronophage's story reset.
      // Fishing here requires a fishing pole in the bag; otherwise show a
      // clear "need a pole" prompt.
      toast("🎣 You need a fishing pole in your bag to fish here.", { duration: 2200 });
      return;
    }
    if (nearNpc) { openDialogue(nearNpc); return; }
    if (nearDoor) { enterDoor(nearDoor); return; }
    // eslint-disable-next-line
  }, [dialogue, screen, nearFountain, nearNpc, nearDoor, enterDoor]);

  /* ── Keyboard ─── */
  useEffect(() => {
    const down = (e) => {
      const k = e.key.toLowerCase();
      if (["w","a","s","d","arrowup","arrowdown","arrowleft","arrowright","e"," ","escape"].includes(k)) e.preventDefault();
      keys.current[k] = true;
      if (k === "e") tryInteract();
      if (k === "escape") setFrontGateOpen(false);
    };
    const up = (e) => { keys.current[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [tryInteract]);

  /* ── Stage tap ─── */
  const onStageTap = (e) => {
    if (dialogue) { advanceDialogue(); return; }
    const rect = stageRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const wx = cx / scale;
    const wy = cy / scale;
    if (screen === "courtyard") {
      const hit = COURTYARD_DOORS.find((d) =>
        wx >= d.x - d.w / 2 && wx <= d.x + d.w / 2 &&
        wy >= d.y - d.h / 2 && wy <= d.y + d.h / 2
      );
      if (hit) { enterDoor(hit); return; }
      // Tap directly on the fountain → fishing (needs a pole)
      const dx = wx - FOUNTAIN.x, dy = wy - FOUNTAIN.y;
      if (dx * dx + dy * dy < FOUNTAIN.r * FOUNTAIN.r * 1.3) {
        toast("🎣 You need a fishing pole in your bag to fish here.", { duration: 2200 });
        return;
      }
    }
    targetRef.current = { x: wx, y: wy };
    rippleRef.current = { x: wx, y: wy, t: performance.now() };
  };

  /* ── Loop ─── */
  useEffect(() => {
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      let vx = 0, vy = 0;
      if (!dialogue && !frontGateOpen) {
        if (keys.current["w"] || keys.current["arrowup"])    vy -= 1;
        if (keys.current["s"] || keys.current["arrowdown"])  vy += 1;
        if (keys.current["a"] || keys.current["arrowleft"])  vx -= 1;
        if (keys.current["d"] || keys.current["arrowright"]) vx += 1;
        vx += joyVec.current.x;
        vy += joyVec.current.y;
        if (vx !== 0 || vy !== 0) targetRef.current = null;
        else if (targetRef.current) {
          const t = targetRef.current;
          const dx = t.x - heroRef.current.x, dy = t.y - heroRef.current.y;
          const d = Math.hypot(dx, dy);
          if (d < 4) targetRef.current = null;
          else { vx = dx / d; vy = dy / d; }
        }
      }
      const mag = Math.hypot(vx, vy);
      const moving = mag > 0.01 && !dialogue && !frontGateOpen;
      if (moving) {
        vx /= mag; vy /= mag;
        if (Math.abs(vx) > Math.abs(vy)) facingRef.current = vx > 0 ? "right" : "left";
        else                              facingRef.current = vy > 0 ? "down"  : "up";
        const nx = heroRef.current.x + vx * SPEED * dt;
        if (!collides(nx, heroRef.current.y, screen)) heroRef.current.x = nx;
        const ny = heroRef.current.y + vy * SPEED * dt;
        if (!collides(heroRef.current.x, ny, screen)) heroRef.current.y = ny;
        walkTimerRef.current += dt;
        if (walkTimerRef.current > 0.16) {
          walkTimerRef.current = 0;
          walkFrameRef.current = walkFrameRef.current === 1 ? 2 : 1;
        }
        // Room exits (leave rooms back to courtyard)
        const exits = SCREENS[screen].exits;
        for (const ex of exits) {
          if (heroRef.current.x >= ex.x && heroRef.current.x <= ex.x + ex.w
              && heroRef.current.y >= ex.y && heroRef.current.y <= ex.y + ex.h) {
            transitionTo(ex.back, ex.spawn);
            break;
          }
        }
      } else {
        walkFrameRef.current = 0;
        idleBobRef.current = Math.sin(now / 380) * 1.2;
      }
      movingRef.current = moving;

      // Near-door + near-fountain + near-npc detection (courtyard only for doors/fountain)
      if (screen === "courtyard") {
        let bestDoor = null, bestDist = 96 * 96;
        for (const d of COURTYARD_DOORS) {
          const dx = d.x - heroRef.current.x;
          const dy = (d.y + d.h / 2) - heroRef.current.y;
          const dd = dx * dx + dy * dy;
          if (dd < bestDist) { bestDist = dd; bestDoor = d; }
        }
        setNearDoor((prev) => (prev?.id === bestDoor?.id ? prev : bestDoor));
        const fdx = FOUNTAIN.x - heroRef.current.x;
        const fdy = FOUNTAIN.y - heroRef.current.y;
        const nf = (fdx * fdx + fdy * fdy) < (FOUNTAIN.r + 30) * (FOUNTAIN.r + 30);
        setNearFountain((p) => (p === nf ? p : nf));
        setNearNpc((p) => (p == null ? p : null));
      } else {
        setNearDoor((p) => (p == null ? p : null));
        setNearFountain((p) => (p === false ? p : false));
        const npcs = SCREENS[screen].npcs || [];
        let near = null;
        for (const n of npcs) {
          const dx = n.x - heroRef.current.x, dy = n.y - heroRef.current.y;
          if (dx * dx + dy * dy < 70 * 70) { near = n; break; }
        }
        setNearNpc((prev) => (prev?.id === near?.id ? prev : near));
      }

      if (dialogue) {
        const tw = typewriterRef.current;
        if (tw.i < tw.full.length && now - tw.last > 22) {
          tw.i = Math.min(tw.full.length, tw.i + 1);
          tw.last = now;
          setDialogue((d) => d && ({ ...d, typed: tw.full.slice(0, tw.i) }));
        }
      }

      // 👥 Peer interpolation — ease every tracked peer's current (x,y)
      // toward its latest server target (tx,ty). dt-based lerp so the
      // speed is framerate-independent. ~6 units/frame-ish feels right
      // for a 2-second poll interval over a 960×640 world.
      {
        const map = peerStateRef.current;
        const alpha = Math.min(1, dt * 6); // dt is already clamped ≤ 0.05
        for (const p of map.values()) {
          const dx = p.tx - p.x;
          const dy = p.ty - p.y;
          const dist = Math.hypot(dx, dy);
          // 🚶 Live walk cycle — if the peer is actively being pulled
          // toward a new target they are "moving"; drive the 2-frame
          // walk cycle and update facing from motion. When stopped,
          // let them idle-bob.
          const moving = dist > 1.5;
          if (moving) {
            if (Math.abs(dx) > Math.abs(dy)) p.facing = dx > 0 ? "right" : "left";
            else                              p.facing = dy > 0 ? "down"  : "up";
            p._walkTimer = (p._walkTimer || 0) + dt;
            if (p._walkTimer > 0.16) {
              p._walkTimer = 0;
              p._walkFrame = p._walkFrame === 1 ? 2 : 1;
            }
            p._idleBob = 0;
            p.moving = true;
          } else {
            p.facing = p.tf || p.facing;
            p._walkFrame = 0;
            p._walkTimer = 0;
            // small sinusoidal bob while idle — offset by hashed name
            // so peers don't bob in sync like a chorus line.
            const seed = (p.name || "").length;
            p._idleBob = Math.sin((now + seed * 180) / 380) * 1.2;
            p.moving = false;
          }
          p.x += dx * alpha;
          p.y += dy * alpha;
        }
      }
      setTick((t) => (t + 1) % 100000);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [dialogue, frontGateOpen, screen, transitionTo]);

  /* ── Joystick ─── */
  const joyBaseRef = useRef(null);
  const joyThumbRef = useRef(null);
  const joyActiveRef = useRef(false);
  const joyOriginRef = useRef({ x: 0, y: 0 });
  const onJoyStart = (e) => {
    joyActiveRef.current = true;
    const rect = joyBaseRef.current.getBoundingClientRect();
    joyOriginRef.current = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    onJoyMove(e);
  };
  const onJoyMove = (e) => {
    if (!joyActiveRef.current) return;
    const t = e.touches ? e.touches[0] : e;
    const dx = t.clientX - joyOriginRef.current.x;
    const dy = t.clientY - joyOriginRef.current.y;
    const max = 40;
    const d = Math.hypot(dx, dy);
    const clamped = d > max ? max / d : 1;
    const cx = dx * clamped, cy = dy * clamped;
    if (joyThumbRef.current) joyThumbRef.current.style.transform = `translate(${cx}px, ${cy}px)`;
    joyVec.current = { x: cx / max, y: cy / max };
  };
  const onJoyEnd = () => {
    joyActiveRef.current = false;
    joyVec.current = { x: 0, y: 0 };
    if (joyThumbRef.current) joyThumbRef.current.style.transform = `translate(0px, 0px)`;
  };

  const scr = SCREENS[screen];
  const ripple = rippleRef.current;
  const rippleAge = ripple ? (performance.now() - ripple.t) : 9999;
  const promptKey = isTouch ? "Tap A" : "Press E";

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100 flex flex-col items-center py-3 px-2" data-testid="roaming-zone-page">
      <div className="w-full max-w-[960px] flex items-center justify-between px-1 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-emerald-400">🏰 Dragonious Castle</div>
          <h1 className="text-xl md:text-2xl font-black" data-testid="roaming-screen-title">{scr.title}</h1>
          <p className="text-[11px] text-neutral-400 italic mt-0.5">
            {screen === "courtyard"
              ? `Walk to a door and ${promptKey} — or tap directly. Try the fountain.`
              : `${promptKey} to talk. Step through the doorway below to return to the courtyard.`}
          </p>
        </div>
      </div>

      <div
        ref={stageRef}
        onClick={onStageTap}
        onTouchStart={onStageTap}
        className="relative w-full max-w-[960px] rounded-xl overflow-hidden border border-neutral-700 shadow-2xl select-none"
        style={{ aspectRatio: `${W} / ${H}`, touchAction: "none", cursor: "pointer" }}
        data-testid="roaming-stage"
      >
        <div className="absolute inset-0">
          {screen === "courtyard" && <CourtyardBackground />}
          {screen === "library"   && <LibraryBackground />}
          {screen === "diner"     && <DinerBackground />}
          {screen === "gym"       && <GymBackground />}
        </div>

        <div className="absolute inset-0" style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: W, height: H }}>
          {ripple && rippleAge < 700 && (
            <div style={{
              position: "absolute", left: ripple.x - 20, top: ripple.y - 20,
              width: 40, height: 40, borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.85)",
              transform: `scale(${1 + rippleAge / 700})`,
              opacity: 1 - rippleAge / 700, pointerEvents: "none",
            }} data-testid="tap-ripple" />
          )}

          {/* Courtyard doors */}
          {screen === "courtyard" && COURTYARD_DOORS.map((d) => (
            <div key={d.id}
                 style={{ position: "absolute", left: d.x - d.w / 2, top: d.y - d.h / 2, width: d.w, height: d.h, pointerEvents: "none" }}
                 data-testid={`door-${d.id}`}>
              <div style={{ position: "absolute", top: -18, left: -30, width: d.w + 60, textAlign: "center", fontSize: 11, color: "#fff", textShadow: "0 1px 2px #000", fontWeight: 700 }}>
                {d.label}{d.visualOnly ? " 🔒" : ""}
              </div>
              <DoorSprite door={d} near={nearDoor?.id === d.id} visualOnly={d.visualOnly} isFrontGate={d.isFrontGate} />
            </div>
          ))}

          {/* Room NPCs (Cari lives here) */}
          {(scr.npcs || []).map((n) => (
            <div key={n.id}
                 style={{ position: "absolute", left: n.x - HERO_W / 2, top: n.y - HERO_H, width: HERO_W, height: HERO_H + 8, pointerEvents: "none" }}
                 data-testid={`npc-${n.id}`}>
              <div style={{ position: "absolute", top: -20, left: -30, width: HERO_W + 60, textAlign: "center", fontSize: 12, color: "#fff", textShadow: "0 1px 2px #000", fontWeight: 700 }}>
                {n.name}
              </div>
              {n.kind === "pitbull-grandma" && <PitbullCariSprite />}
              {n.kind === "chef-david"      && <ChefDavidSprite />}
              {n.kind === "honey-badger"    && <HoneyBadgerNathanielSprite />}
            </div>
          ))}

          {/* Hero */}
          <div style={{ position: "absolute", left: heroRef.current.x - HERO_W / 2, top: heroRef.current.y - HERO_H, width: HERO_W, height: HERO_H, pointerEvents: "none" }} data-testid="hero-sprite">
            <HeroSprite facing={facingRef.current} moving={movingRef.current} frame={walkFrameRef.current} bob={idleBobRef.current} />
          </div>

          {/* 👥 Online peers — rendered in whichever room they're
              broadcasting from. The server already filters peers to the
              current screen (via ?screen=<room>). Each peer is drawn at
              their LIVE roam position (smoothly interpolated between
              2 s polls) with a nameplate + handle + pet badge. */}
          {Array.from(peerStateRef.current.values()).map((p) => {
            // Resolve by name since map keys aren't in render loop.
            // Each entry stores its own name/handle fields.
            const lastSeenAgo = _formatLastSeen(p.last_seen_at);
            const uid = p.name || "peer";
            return (
              <div
                key={uid + "_" + (p.handle || "")}
                data-testid={`hub-peer-${p.handle || p.name}`}
                style={{
                  position: "absolute",
                  left: p.x - HERO_W / 2,
                  top: p.y - HERO_H,
                  width: HERO_W,
                  pointerEvents: "none",
                  transition: "none",
                }}
              >
                {/* Floating nameplate */}
                <div style={{
                  position: "absolute", left: -30, top: -34, width: HERO_W + 60,
                  padding: "3px 6px", borderRadius: 6,
                  background: "rgba(0,0,0,0.75)", border: "1px solid rgba(96,165,250,0.6)",
                  color: "#dbeafe", fontSize: 10, fontWeight: 800, textAlign: "center",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {p.handle ? `@${p.handle}` : p.name}
                  {p.social_handle && (
                    <div style={{ fontSize: 8, color: "#93c5fd", fontWeight: 600 }}>
                      {p.social_handle}
                    </div>
                  )}
                  {lastSeenAgo && (
                    <div style={{ fontSize: 8, color: "#22c55e", fontWeight: 600 }}>
                      🟢 {lastSeenAgo}
                    </div>
                  )}
                </div>
                {/* Peer avatar — tinted chibi, drives the same walk/idle
                    cycle as the local hero. Moving peers step through
                    the 2-frame walk; idle peers do a staggered bob. */}
                <div style={{ filter: "hue-rotate(80deg) saturate(0.85) brightness(0.9)" }}>
                  <HeroSprite
                    facing={p.facing || "down"}
                    moving={!!p.moving}
                    frame={p._walkFrame || 0}
                    bob={p._idleBob || 0}
                  />
                </div>
                {/* Pet-icon badge */}
                <div style={{
                  position: "absolute", right: -6, bottom: 6, width: 20, height: 20,
                  borderRadius: 10, background: "rgba(0,0,0,0.85)",
                  border: "1px solid rgba(252,211,77,0.7)",
                  fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center",
                }}>{p.pet_icon || "🦸"}</div>
              </div>
            );
          })}

          {/* Prompts */}
          {(nearDoor || nearFountain || nearNpc) && !dialogue && (
            <div style={{
              position: "absolute",
              left: (nearNpc ? nearNpc.x : nearFountain ? FOUNTAIN.x : nearDoor.x) - 44,
              top:  (nearNpc ? nearNpc.y - HERO_H - 44
                     : nearFountain ? FOUNTAIN.y + 96
                     : nearDoor.y + nearDoor.h / 2 + 8),
              width: 88, padding: "4px 8px", borderRadius: 8,
              background: "rgba(0,0,0,0.8)", border: `2px solid ${nearFountain ? "#fcd34d" : "#60a5fa"}`,
              color: nearFountain ? "#fef3c7" : "#dbeafe",
              fontWeight: 800, fontSize: 12, textAlign: "center",
              animation: "bounceUpDown 0.9s ease-in-out infinite",
            }} data-testid="interact-prompt">
              {nearFountain ? `${promptKey} · descend` : promptKey}
            </div>
          )}
        </div>

        {/* Fade */}
        <div className="absolute inset-0 bg-black pointer-events-none"
             style={{ opacity: fading ? 1 : 0, transition: `opacity ${FADE_MS / 2}ms linear` }}
             data-testid="screen-fade" />

        {/* Dialogue */}
        {dialogue && (
          <div className="absolute left-3 right-3 md:left-6 md:right-6 bottom-3 md:bottom-6" data-testid="dialogue-box" style={{ zIndex: 30 }}>
            <div onClick={(e) => { e.stopPropagation(); advanceDialogue(); }}
                 onTouchStart={(e) => e.stopPropagation()}
                 style={{ background: "linear-gradient(180deg, #002099 0%, #0044cc 100%)", border: "3px solid #ffffff", borderRadius: 10, padding: "14px 16px", color: "#ffffff", boxShadow: "0 8px 24px rgba(0,0,0,0.6)", cursor: "pointer" }}>
              <div style={{ fontSize: 12, letterSpacing: 2, textTransform: "uppercase", color: "#bfdbfe", marginBottom: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }} data-testid="dialogue-speaker">
                <span>{dialogue.npc.name}</span>
                {/* 🃏 DFCL duelist marker — read-only tag per owner spec.
                    No "challenge" button; gameplay comes later. */}
                {dialogue.npc.dfcl_duelable && (
                  <span
                    data-testid="dfcl-duelist-badge"
                    style={{
                      fontSize: 9, letterSpacing: 1.5, color: "#fde68a",
                      border: "1px solid rgba(252, 211, 77, 0.5)",
                      background: "rgba(120, 53, 15, 0.35)",
                      padding: "1px 6px", borderRadius: 2,
                    }}
                    title="Future Flame Card Legends duelist"
                  >
                    🃏 DFCL Duelist
                  </span>
                )}
              </div>
              <div style={{ fontSize: 15, minHeight: 44, lineHeight: 1.4 }} data-testid="dialogue-text">
                {dialogue.typed}
                <span style={{ opacity: dialogue.typed.length >= (typewriterRef.current.full || "").length ? 1 : 0, marginLeft: 6 }}>▾</span>
              </div>
              <div style={{ fontSize: 11, color: "#bfdbfe", marginTop: 6, textAlign: "right" }}>
                {typewriterRef.current.i < (typewriterRef.current.full || "").length ? `${promptKey} to skip` : `${promptKey} to continue`}
              </div>
            </div>
          </div>
        )}

        {/* Front Gate modal */}
        {frontGateOpen && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-6" data-testid="front-gate-modal" style={{ zIndex: 35 }} onClick={(e) => e.stopPropagation()}>
            <div className="bg-neutral-900 border-2 border-amber-400 rounded-xl p-5 max-w-sm w-full text-center">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">🏰 Front Castle Gate</div>
              <div className="text-lg font-black mb-4">Step outside — where to?</div>
              <div className="flex flex-col gap-3">
                <button onClick={(e) => { e.stopPropagation(); nav("/wilds"); }}
                        data-testid="front-gate-wilds"
                        className="px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm">
                  🌾 Wilds of Aethelgard
                </button>
                <button onClick={(e) => { e.stopPropagation(); nav("/oasis"); }}
                        data-testid="front-gate-oasis"
                        className="px-4 py-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm">
                  🏝️ The Oasis (fishing)
                </button>
                <button onClick={(e) => { e.stopPropagation(); setFrontGateOpen(false); }}
                        data-testid="front-gate-cancel"
                        className="mt-1 px-4 py-2 rounded-lg bg-neutral-800 text-neutral-400 text-xs">
                  Back to the courtyard
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Joystick (mobile) */}
        <div ref={joyBaseRef}
             onTouchStart={(e) => { e.stopPropagation(); onJoyStart(e); }}
             onTouchMove={(e) => { e.stopPropagation(); onJoyMove(e); }}
             onTouchEnd={(e) => { e.stopPropagation(); onJoyEnd(); }}
             onTouchCancel={onJoyEnd}
             onMouseDown={(e) => { e.stopPropagation(); onJoyStart(e); }}
             onMouseMove={onJoyMove}
             onMouseUp={onJoyEnd}
             onClick={(e) => e.stopPropagation()}
             data-testid="virtual-joystick"
             className="absolute bottom-3 left-3 md:hidden"
             style={{ width: 110, height: 110, borderRadius: "50%", background: "rgba(0,0,0,0.35)", border: "2px solid rgba(255,255,255,0.4)", touchAction: "none", zIndex: 20 }}>
          <div ref={joyThumbRef}
               style={{ position: "absolute", left: 35, top: 35, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.85)", border: "2px solid #1e3a8a", transition: "transform 0.05s linear", pointerEvents: "none" }} />
        </div>

        {/* A button (mobile) */}
        <button type="button"
                onClick={(e) => { e.stopPropagation(); tryInteract(); }}
                onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); tryInteract(); }}
                data-testid="action-a-button"
                className="absolute bottom-6 right-4 md:hidden"
                style={{ width: 72, height: 72, borderRadius: "50%", background: (nearDoor || nearFountain || nearNpc) ? "rgba(59,130,246,0.85)" : "rgba(0,0,0,0.55)", border: "3px solid #ffffff", color: "#fff", fontWeight: 900, fontSize: 26, zIndex: 20 }}>
          A
        </button>

        <div className="hidden md:block absolute bottom-2 right-3 text-[10px] text-white/70 bg-black/40 px-2 py-1 rounded" data-testid="controls-hint">
          WASD / Arrows · Click a door · E to enter · tap the fountain for secrets
        </div>
      </div>

      <style>{`
        @keyframes bounceUpDown { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
      `}</style>
    </div>
  );
}
